import { defineStore } from 'pinia'
import userApi from '@/services/user/api'
import {
  clearAuth,
  getAuthSession,
  getAuthUser,
  getAuthUserId,
  setAuthSession,
  setAuthUser
} from '@/services/auth'

const normalizeUserId = (user = {}) =>
  user?.s_id ||
  user?.id ||
  user?.user_id ||
  user?.vendor_id ||
  user?.vendorId ||
  ''

const firstProfileValue = (...values) => {
  const value = values.find((item) => String(item || '').trim())
  return value ? String(value).trim() : ''
}

const normalizeProfile = (user = {}) => {
  const name = firstProfileValue(user?.name, user?.s_name, user?.full_name, user?.fullname)
  const username = firstProfileValue(user?.username, user?.s_username)
  const email = firstProfileValue(user?.email, user?.s_email)
  const phone = firstProfileValue(user?.phone, user?.s_phone)
  const userImage = firstProfileValue(user?.user_img, user?.s_user_img)

  return {
    ...user,
    name,
    s_name: firstProfileValue(user?.s_name, name),
    username,
    s_username: firstProfileValue(user?.s_username, username),
    email,
    s_email: firstProfileValue(user?.s_email, email),
    phone,
    s_phone: firstProfileValue(user?.s_phone, phone),
    user_img: userImage,
    s_user_img: firstProfileValue(user?.s_user_img, userImage)
  }
}

let profileRequest = null

export const useSessionStore = defineStore('session', {
  state: () => ({
    user: getAuthUser() || null,
    profile: getAuthUser() || null,
    userId: getAuthUserId() || '',
    authSession: getAuthSession() || null,
    email: (getAuthUser() || {}).email || (getAuthUser() || {}).s_email || '',
    phone: (getAuthUser() || {}).phone || (getAuthUser() || {}).s_phone || '',
    profileLoaded: false
  }),
  getters: {
    isLoggedIn: (state) => Boolean(state.userId || state.authSession?.loggedIn),
    displayName: (state) =>
      state.profile?.name ||
      state.profile?.s_name ||
      state.user?.name ||
      state.user?.s_name ||
      state.user?.username ||
      state.user?.s_username ||
      'User',
    profileImage: (state) =>
      state.profile?.user_img ||
      state.profile?.s_user_img ||
      state.user?.user_img ||
      state.user?.s_user_img ||
      '/assets/img/av.png'
  },
  actions: {
    setUser(user) {
      if (!user) return
      const previousUserId = this.userId
      const merged = normalizeProfile({
        ...(this.user || {}),
        ...user,
        s_id: normalizeUserId(user) || normalizeUserId(this.user || {})
      })
      this.user = merged
      this.profile = { ...(this.profile || {}), ...merged }
      this.userId = normalizeUserId(merged)
      if (previousUserId && String(previousUserId) !== String(this.userId)) {
        this.profileLoaded = false
      }
      this.authSession = { ...(this.authSession || {}), loggedIn: true, role: 'user', type: 'user', s_id: this.userId }
      this.email = merged.email || merged.s_email || this.email || ''
      this.phone = merged.phone || merged.s_phone || this.phone || ''
      setAuthUser({ ...merged, role: 'user', type: 'user' })
      setAuthSession({ loggedIn: true, role: 'user', type: 'user', s_id: this.userId })
    },
    async loadUser() {
      this.authSession = getAuthSession() || null
      const storedUser = getAuthUser() || null
      const user = storedUser ? normalizeProfile(storedUser) : null
      this.user = user
      this.profile = user || this.profile || null
      this.userId = getAuthUserId() || normalizeUserId(user || {})
      this.email = user?.email || user?.s_email || ''
      this.phone = user?.phone || user?.s_phone || ''
      return user
    },
    async fetchProfile(force = false) {
      if (!this.userId) {
        await this.loadUser()
      }
      if (!this.userId) return null
      if (!force && this.profileLoaded) return this.profile
      if (profileRequest) return profileRequest

      const requestedUserId = this.userId
      profileRequest = (async () => {
        try {
          const formData = new FormData()
          formData.append('id', requestedUserId)
          const { data } = await userApi.getProfileApi(formData)
          const failed = data?.status === 0 || data?.status === false || data?.status === 'error' || data?.success === false
          if (failed || String(this.userId) !== String(requestedUserId)) {
            return this.profile || this.user || null
          }
          const profile = data?.user || data?.data?.user || data?.data?.[0] || data?.profile || data?.data || null
          if (profile && typeof profile === 'object' && !Array.isArray(profile)) {
            this.setUser(profile)
            this.profileLoaded = true
          }
          return this.profile || this.user || null
        } catch (error) {
          console.error('Profile fetch failed:', error)
          return this.profile || this.user || null
        } finally {
          profileRequest = null
        }
      })()

      return profileRequest
    },
    updateProfileLocally(patch = {}) {
      this.profile = normalizeProfile({ ...(this.profile || {}), ...patch })
      this.user = normalizeProfile({ ...(this.user || {}), ...patch })
      this.email = this.profile?.email || this.profile?.s_email || this.email || ''
      this.phone = this.profile?.phone || this.profile?.s_phone || this.phone || ''
      setAuthUser({ ...(this.user || {}), ...(this.profile || {}), role: 'user', type: 'user' })
    },
    logout() {
      this.user = null
      this.profile = null
      this.userId = ''
      this.authSession = null
      this.email = ''
      this.phone = ''
      this.profileLoaded = false
      profileRequest = null
      clearAuth()
    }
  }
})
